<template>
  <div class="receipt-details-lookup">
    <p>Please confirm your card information to view this receipt.</p>

    <div class="form-group form-group-lg row">
      <label
        for="panLastFour"
        class="col-12 col-sm-5 offset-sm-1 col-form-label"
        >Card number last four digits:</label
      >
      <div class="col-8 col-sm-4">
        <input
          type="tel"
          id="panLastFour"
          class="form-control form-control-lg"
          placeholder="- - - -"
          aria-label="PAN last four digits"
          maxlength="4"
          v-model="panLastFour"
          autofocus
          :disabled="searching"
        />
      </div>
      <div class="col-4 col-sm-2">
        <font-awesome-icon
          v-if="searching"
          icon="spinner"
          spin
          class="mt-2"
          size="lg"
        ></font-awesome-icon>
        <font-awesome-icon
          v-if="!searching"
          icon="credit-card"
          class="mt-2"
          size="lg"
        ></font-awesome-icon>
      </div>
    </div>

    <div class="row" v-if="searching">
      <div class="col-12">
        <span>{{ searchProgressText }}</span>
        <div class="progress">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            :aria-valuenow="searchProgressPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="`width: ${searchProgressPercentage}%`"
          ></div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 ">
        <button
          class="btn btn-link btn-block"
          type="button"
          data-toggle="collapse"
          data-target="#mobile-wallet-help"
          aria-expanded="false"
          aria-controls="mobile-wallet-help"
        >
          Used Apple Pay or Google Pay?
        </button>
        <div
          id="mobile-wallet-help"
          class="collapse text-left card p-4 bg-light"
        >
          <p>
            Please provide your mobile wallet's virtual card number to view your
            receipt. Here's how to find it:
          </p>
          <h5>Google Pay</h5>
          <ul>
            <li>Open the Google Pay app.</li>
            <li>Tap the card you used for this transaction.</li>
            <li>
              Scroll down to the bottom
            </li>
            <li>
              The Virtual Account Number (last 4 digits only) is displayed.
            </li>
            <li>
              Please see
              <a
                href="https://support.google.com/pay/answer/7643925?hl=en#zippy=%2Cvirtual-account-numbers"
                target="_blank"
                >this support article</a
              >
              for further help.
            </li>
          </ul>

          <h5>Apple Pay</h5>
          <ul>
            <li>Open the Wallet app.</li>
            <li>Tap the card you used for this transaction.</li>
            <li>Tap the More button.</li>
            <li>
              This screen will display the last four digits of the Device
              Account Number for your payment card.
            </li>
            <li>
              Please see
              <a href="https://support.apple.com/en-gb/HT212779" target="_blank"
                >this support article</a
              >
              for further help.
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="error && !searching" class="alert alert-warning" role="alert">
      Error: {{ error }}
    </div>

    <button
      v-if="error && !searching"
      class="btn btn-info btn-block"
      @click="searchReceipts"
    >
      Try again
      <font-awesome-icon icon="undo" class="ml-2"></font-awesome-icon>
    </button>
  </div>
</template>

<script>
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: "ReceiptDetailsLookup",
  props: {
    receiptSlug: String,
    receiptId: String,
  },
  data() {
    return {
      panLastFour: "",
      error: null,
      searching: false,
      searchProgressPercentage: 0,
      searchProgressText: "",
    };
  },
  watch: {
    panLastFour: function(val) {
      if (val && val.length === 4) {
        if (this.receiptSlug) {
          this.searchReceipts();
        } else if (this.receiptId) {
          this.navigateToReceipt(this.receiptId, val);
        }
      }
    },
  },
  methods: {
    async searchReceipts() {
      // handle issues covered by trailing slash in base URL (causing slug to start with leading slash)
      let slug = this.receiptSlug;
      if (slug.startsWith("/")) {
        slug = slug.split("/")[1];
      }

      this.searching = true;

      const attempts = [
        {
          expectedCompletionSeconds: 10,
          afterFailureWaitSeconds: 1,
          progressText: "Looking up transaction.",
        },
        {
          expectedCompletionSeconds: 11,
          afterFailureWaitSeconds: 2,
          progressText: "Getting things ready...",
        },
        {
          expectedCompletionSeconds: 12,
          afterFailureWaitSeconds: 3,
          progressText: "Generating your receipt.",
        },
        {
          expectedCompletionSeconds: 13,
          afterFailureWaitSeconds: 4,
          progressText: "This won't take long.",
        },
        {
          expectedCompletionSeconds: 14,
          afterFailureWaitSeconds: 5,
          progressText: "Still working on it!",
        },
        {
          expectedCompletionSeconds: 15,
          afterFailureWaitSeconds: 6,
          progressText: "This might take a minute.",
        },
        {
          expectedCompletionSeconds: 16,
          afterFailureWaitSeconds: 7,
          progressText: "Sorry for the delay.",
        },
        {
          expectedCompletionSeconds: 17,
          afterFailureWaitSeconds: 8,
          progressText: "This is taking longer than usual.",
        },
        {
          expectedCompletionSeconds: 18,
          afterFailureWaitSeconds: 9,
          progressText: "It should be along in a jiffy.",
        },
        {
          expectedCompletionSeconds: 19,
          afterFailureWaitSeconds: 10,
          progressText: "Just need a little more time.",
        },
        {
          expectedCompletionSeconds: 20,
          afterFailureWaitSeconds: 11,
          progressText: "Hmm, something seems to be wrong",
        },
        {
          expectedCompletionSeconds: 21,
          afterFailureWaitSeconds: 12,
          progressText: "Still trying for you...",
        },
        {
          expectedCompletionSeconds: 22,
          afterFailureWaitSeconds: 13,
          progressText: "It doesn't usually take this long",
        },
        {
          expectedCompletionSeconds: 23,
          afterFailureWaitSeconds: 14,
          progressText: "The cogs are whirring...",
        },
        {
          expectedCompletionSeconds: 24,
          afterFailureWaitSeconds: 15,
          progressText: "We might need to try again.",
        },
        {
          expectedCompletionSeconds: 25,
          afterFailureWaitSeconds: 20,
          progressText: "Trying one more time...",
        },
      ];

      let elapsedTime = 0;
      console.log("Starting timer");

      // loop for progress indicator
      let expectedCompletionMs = attempts[0].expectedCompletionSeconds * 1000;
      const intervalMs = 500;
      const interval = setInterval(() => {
        elapsedTime += intervalMs;

        // calculate progress
        this.searchProgressPercentage = Math.round(
          (elapsedTime / expectedCompletionMs) * 100
        );
        console.log(
          `${elapsedTime}ms elapsed of expected ${expectedCompletionMs}ms (${this.searchProgressPercentage}%)`
        );
      }, intervalMs);

      for (const attempt of attempts) {
        expectedCompletionMs = attempt.expectedCompletionSeconds * 1000;

        this.searchProgressText = attempt.progressText;

        // make the request
        try {
          let response;
          response = await this.api.get("/v1/receipts", {
            params: {
              slug,
              panLastFour: this.panLastFour,
              now: new Date().toISOString(),
            },
          });

          if (response.data && response.data.length > 0) {
            // clear interval, set progress to 100
            this.searchProgressPercentage = 100;
            clearInterval(interval);

            this.searching = false;

            // a receipt has been found
            const receipt = response.data[0];
            this.navigateToReceipt(receipt.id, this.panLastFour);
            return null;
          } else {
            // wait a bit
            console.log(
              `waiting ${attempt.afterFailureWaitSeconds} seconds before trying again`
            );

            const delaySeconds =
              attempt.expectedCompletionSeconds +
              attempt.afterFailureWaitSeconds;
            const delayMs = delaySeconds * 1000;
            expectedCompletionMs = elapsedTime + delayMs;
            await sleep(attempt.afterFailureWaitSeconds * 1000);
          }
        } catch (error) {
          // clear interval, set progress to 0
          this.searchProgressPercentage = 0;
          clearInterval(interval);
          this.searching = false;
          if (error.response && error.response.data && error.response.data) {
            this.error = error.response.data.message;
          } else if (error.message) {
            this.error = error.message;
          } else {
            this.error = "An unknown error occurred.";
          }
        }
      }

      // all attempts done, no result
      if (!this.error) {
        // still no joy
        // clear interval, set progress to 0
        this.searchProgressPercentage = 0;
        clearInterval(interval);
        this.searching = false;
        this.error =
          "Sorry, we couldn't find your receipt. If the transaction has recently been processed, we might need a bit more time to generate it. Please try again in a few moments.";
      }
    },
    navigateToReceipt(receiptId, panLastFour) {
      this.$router.push({
        path: `/receipts/${receiptId}`,
        query: { panLastFour: panLastFour },
      });
    },
  },
  created() {
    // check the query param
    if (this.$route.query && this.$route.query.panLastFour) {
      this.panLastFour = this.$route.query.panLastFour;
    }
  },
};
</script>
